import {
	Button,
	Divider,
	Heading,
	IconButton,
	useDisclosure,
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Tag,
	TagLabel,
} from "@chakra-ui/react";
import clsx from "clsx";
import { format } from "date-fns";
import { Edit2, Pin } from "lucide-react";
import React from "react";
import { formatAsNaira, getCappedAmount } from "src/utlis/helpers";
import { PortfolioEmploymentHistoryItem } from "src/types";
import { EmploymentHistoryEditor } from "./employment-history-editor";
import { ExpandableText } from "@/components/expandable-text";
interface EmploymentHistoryItemProps
	extends React.DetailedHTMLProps<
		React.HTMLAttributes<HTMLDivElement>,
		HTMLDivElement
	> {
	item: PortfolioEmploymentHistoryItem;
	editable?: boolean;
	pinned?: boolean;
	canPin?: boolean;
	onPinToggle?: () => void;
	isLoading?: boolean;
	onSaved?: () => void;
}

export function EmploymentHistoryItem({
	item,
	className,
	editable,
	pinned,
	canPin,
	onPinToggle,
	isLoading,
	onSaved,
}: EmploymentHistoryItemProps) {
	const { isOpen, onOpen, onClose } = useDisclosure();

	const {
		companyName,
		jobTitle,
		description,
		startedAt,
		endedAt,
		monthlySalary,
		proDoesNotWishToDiscloseMonthlySalary,
		id,
	} = item;

	const dateDisplay = () => {
		const startDate = format(new Date(startedAt), "MMM yyyy");
		if (endedAt === null) {
			return `${startDate} - Present`;
		}
		const endDate = format(new Date(endedAt), "MMM yyyy");
		return `${startDate} - ${endDate}`;
	};

	return (
		<>
			{!!id && editable && isOpen && (
				<Modal
					isOpen={isOpen}
					onClose={onClose}
					isCentered
					closeOnOverlayClick
					size="lg"
				>
					<ModalOverlay />
					<ModalContent>
						<ModalHeader className="font-[AeonikPro] !text-2xl">
							Edit Employment
						</ModalHeader>
						<Divider />
						<ModalBody>
							<EmploymentHistoryEditor
								item={item}
								onClose={onClose}
								onSaved={onSaved}
							/>
						</ModalBody>
					</ModalContent>
				</Modal>
			)}
			<div
				className={clsx(" grid grid-cols-[1fr,auto] border-b py-4", className)}
			>
				<div className={clsx("flex flex-col gap-4 ")}>
					<div className="flex flex-col gap-2 items-start">
						{pinned && (
							<Tag
								size="lg"
								display="flex"
								alignItems="center"
								borderRadius="full"
								colorScheme="brand"
							>
								<Pin className="w-4 mr-2 rotate-45 fill-flatteredFlamingo-600" />
								<TagLabel fontWeight="500">Pinned</TagLabel>
							</Tag>
						)}
						<div className="flex flex-col lg:flex-row items-start lg:items-center gap-1">
							<Heading as="h4" fontSize={18} className="font-medium m-0">
								{jobTitle} at {companyName}
							</Heading>
						</div>

						<div className="flex flex-col lg:flex-row gap-1 lg:gap-4">
							<div className="flex items-center gap-2 lg:gap-4">
								{!proDoesNotWishToDiscloseMonthlySalary && monthlySalary ? (
									<p>
										Monthly Salary:{" "}
										{editable
											? formatAsNaira(monthlySalary, 2)
											: `${formatAsNaira(getCappedAmount(monthlySalary), 0)}+`}
									</p>
								) : (
									<p>Monthly Salary: N/A</p>
								)}
							</div>

							<Divider orientation="vertical" />
							<p>{dateDisplay()}</p>
						</div>
					</div>
					<div className="flex flex-col gap-2">
						{description && <ExpandableText text={description} />}
					</div>
				</div>
				{!!id && editable && (
					<div className="flex flex-col items-end gap-2">
						{(canPin || pinned) && (
							<IconButton
								colorScheme="earthRed"
								variant="ghost"
								aria-label="Pin Employment History"
								icon={
									<Pin
										className={clsx("w-5 h-5", {
											"fill-brand-500": item.isPinned,
										})}
									/>
								}
								onClick={onPinToggle}
								isLoading={isLoading}
							/>
						)}
						<Button
							colorScheme="earthRed"
							variant="ghost"
							leftIcon={<Edit2 className="w-4 h-4" />}
							onClick={onOpen}
						>
							Edit
						</Button>
					</div>
				)}
			</div>
		</>
	);
}
